import { getWorkoutMixedMode, type SlopeIntervals, type WorkoutPlayerType } from '@/Util/api';
import { workoutPlayersThatSupportMixedMode, workoutPlayerTypeToName } from './constants';
import SlopeIntervalsDialog from './SlopeIntervalsDialog.svelte';

export type WorkoutFileFormat = 'zwo' | 'tcx' | 'erg';

const mixedModeByPath = new Map<string, Promise<boolean>>();

/**
 * Whether a workout has slope intervals as well as ERG intervals. Uses `known` when the caller already
 * has the workout's `mixed_mode` flag, otherwise asks the server once per workout.
 */
export function isMixedModeWorkout(path: string, known?: boolean | null): Promise<boolean> {
  if (typeof known === 'boolean') return Promise.resolve(known);
  if (!mixedModeByPath.has(path)) {
    mixedModeByPath.set(path, getWorkoutMixedMode(path).catch((e) => {
      console.error(e);
      mixedModeByPath.delete(path);
      // The server falls back to free ride for a mixed mode workout sent without an option.
      return false;
    }));
  }
  return mixedModeByPath.get(path)!;
}

let dialog: SlopeIntervalsDialog | null = null;
let pending: ((choice: SlopeIntervals | null) => void) | null = null;

function settle(choice: SlopeIntervals | null) {
  const resolve = pending;
  pending = null;
  dialog?.$set({ open: false });
  resolve?.(choice);
}

/** Asks the rider how to send slope intervals. Resolves to null if they cancel. */
export function chooseSlopeIntervals(destination: string, action: 'send' | 'download'): Promise<SlopeIntervals | null> {
  settle(null);
  return new Promise((resolve) => {
    pending = resolve;
    const props = { open: true, destination, action, onSettle: settle };
    if (dialog) dialog.$set(props);
    else dialog = new SlopeIntervalsDialog({ target: document.body, props });
  });
}

/**
 * The slope intervals option to send or download a workout with. Undefined when no choice is needed
 * (the workout has no slope intervals, or the player supports mixed mode), null when the rider cancels.
 */
export async function askSlopeIntervals(
  destination: WorkoutPlayerType | WorkoutFileFormat,
  workout: { path: string; mixed_mode?: boolean | null },
): Promise<SlopeIntervals | null | undefined> {
  if (workoutPlayersThatSupportMixedMode.includes(destination as WorkoutPlayerType)) return undefined;
  if (!(await isMixedModeWorkout(workout.path, workout.mixed_mode))) return undefined;

  const playerName = workoutPlayerTypeToName[destination as WorkoutPlayerType];
  return chooseSlopeIntervals(playerName ?? destination.toUpperCase(), playerName ? 'send' : 'download');
}
